import Entity, { EntityTypes } from 'models/entity';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import { i18nBase } from 'services/Localization/i18n';
import { toLocaleDateTimeMedium } from 'utils/datetime';

export enum ActivityType {
  NotSet = 0,
  RecentlyChanged = 1, //created when an entity has changed
  RecentlyViewed = 2, //future usage
  RecentlyClosed = 3, //created when an task has been completed
  LibraryNewVersion = 4, //create when a new version of a library item is published
  OverDeadline = 5, //created when a task is overdue
  Assigned = 6, //created when a task is assigned to a user
  Refused = 7, //created when a task is rejected by a user
  LibraryReadRequest = 8, //created when a user is requested to confirm a library item
  LibraryItemRead = 9, //created when a user viewed a library item
  RefusedInstance = 10, //created when a user rejects an instance of a task
  Upcoming = 11, //created when a task is upcoming
  LibraryNewVersionOwner = 12, //created when a new version of a library item is published and the user is the owner of a related item
  LinkedControlUpdated = 13, //created when a linked control is updated
  ProcessingActivityApproved = 14, //created when a processing activity is approved
  ProcessingActivityRejected = 15, //created when a processing activity is rejected
  TaskReminder = 16, //created when a task reminder is due
  FollowUpPending = 17, //created when a follow-up is deferred
  AITaskApprovalReminder = 18, //created when an AI task approval reminder is due
  InternalAuditPlanned = 19, //created when an internal audit is planned (participant invitation)
  InternalAuditReport = 20, //created when an internal audit report is shared with the auditees
  InternalAuditConfirmed = 21, //created when the final invitation (definitive team, scope and times) is sent
  AuditCriteriaChanged = 23, //created when a re-evaluation changed the audit criteria of a control, for its owner
  GenericNotification = 90, //created for generic notifications
  UserNotification = 91, //created for user notifications
}

export enum ActivityPriority {
  Informational = 0,
  Low = 1,
  Normal = 2,
  High = 3,
  Critical = 4,
}

export default class Activity {
  activityId: number;

  entity: Entity;

  typeOfActivity: ActivityType;

  created: Date;

  refDateTime?: Date;

  priority: ActivityPriority;

  userId: string;

  refUserId?: string;

  userEmail?: string;

  refUserEmail?: string;

  dismissed?: Date;

  refData?: string;

  primaryText?: string;

  secondaryText?: string;

  tooltipContent?: JSX.Element;

  constructor() {
    this.activityId = 0;
    this.entity = new Entity();
    this.typeOfActivity = ActivityType.NotSet;
    this.created = new Date();
    this.refDateTime = new Date();
    this.priority = ActivityPriority.Informational;
    this.userId = '';
  }

  getUserName(globalDataCache: GlobalDataCache): string {
    const user = globalDataCache.users.items.find((u) => u.id === this.userId);
    if (user === undefined) {
      return this.userEmail ? this.userEmail : '?';
    }

    return user.name;
  }

  getRefUserName(globalDataCache: GlobalDataCache): string {
    if (!this.refUserId) return '';
    const user = globalDataCache.users.items.find((u) => u.id === this.refUserId);
    if (user === undefined) {
      return this.refUserEmail ? this.refUserEmail : '?';
    }

    return user.name;
  }

  getTransParams(globalDataCache: GlobalDataCache) {
    return {
      userName: this.getUserName(globalDataCache),
      refUser: this.getRefUserName(globalDataCache),
      entityName: this.entity.entityName,
      created: toLocaleDateTimeMedium(this.created),
      refDate: toLocaleDateTimeMedium(this.refDateTime),
    };
  }

  getPrimaryText(globalDataCache: GlobalDataCache): string {
    switch (this.entity.typeOfEntity) {
      case EntityTypes.Task:
        switch (this.typeOfActivity) {
          case ActivityType.Assigned:
            return i18nBase.t('activity:Task.Assigned.PrimaryText', this.getTransParams(globalDataCache));
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:Task.RecentlyChanged.PrimaryText', this.getTransParams(globalDataCache));
          case ActivityType.RecentlyClosed:
            return i18nBase.t('activity:Task.RecentlyClosed.PrimaryText', this.getTransParams(globalDataCache));
          case ActivityType.OverDeadline:
            return i18nBase.t('activity:Task.OverDeadline.PrimaryText', this.getTransParams(globalDataCache));
          case ActivityType.TaskReminder:
            return i18nBase.t('activity:Task.Reminder.PrimaryText', this.getTransParams(globalDataCache));
          case ActivityType.Refused:
            return i18nBase.t('activity:Task.Refused.PrimaryText', this.getTransParams(globalDataCache));
          case ActivityType.RefusedInstance:
            return i18nBase.t('activity:Task.RefusedInstance.PrimaryText', this.getTransParams(globalDataCache));
          case ActivityType.Upcoming:
            return i18nBase.t('activity:Task.Upcoming.PrimaryText', this.getTransParams(globalDataCache));
          case ActivityType.FollowUpPending:
            return i18nBase.t('activity:Task.FollowUpPending.PrimaryText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.PrimaryText.NotSet');
        }
      case EntityTypes.Link:
        switch (this.typeOfActivity) {
          case ActivityType.LibraryNewVersionOwner:
            return i18nBase.t('activity:Link.LibraryNewVersionOwner.PrimaryText', this.getTransParams(globalDataCache));
          case ActivityType.LibraryReadRequest:
            return i18nBase.t('activity:Link.LibraryReadRequest.PrimaryText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.PrimaryText.NotSet');
        }
      case EntityTypes.Control:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:Control.RecentlyChanged.PrimaryText', this.getTransParams(globalDataCache));
          case ActivityType.LinkedControlUpdated:
            return i18nBase.t(
              'activity:Control.LinkedControlUpdated.PrimaryText',
              this.getTransParams(globalDataCache),
            );
          case ActivityType.AuditCriteriaChanged:
            return i18nBase.t('activity:Control.AuditCriteriaChanged.PrimaryText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.PrimaryText.NotSet');
        }
      case EntityTypes.Requirement:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:Requirement.RecentlyChanged.PrimaryText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.PrimaryText.NotSet');
        }
      case EntityTypes.Risk:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:Risk.RecentlyChanged.PrimaryText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.PrimaryText.NotSet');
        }
      case EntityTypes.Process:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:Process.RecentlyChanged.PrimaryText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.PrimaryText.NotSet');
        }
      case EntityTypes.Objective:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:Objective.RecentlyChanged.PrimaryText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.PrimaryText.NotSet');
        }
      case EntityTypes.KPI:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:KPI.RecentlyChanged.PrimaryText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.PrimaryText.NotSet');
        }
      case EntityTypes.Asset:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:Asset.RecentlyChanged.PrimaryText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.PrimaryText.NotSet');
        }
      case EntityTypes.ProcessingActivity:
        switch (this.typeOfActivity) {
          case ActivityType.ProcessingActivityApproved:
            return i18nBase.t('activity:ProcessingActivity.Approved.PrimaryText', this.getTransParams(globalDataCache));
          case ActivityType.ProcessingActivityRejected:
            return i18nBase.t('activity:ProcessingActivity.Rejected.PrimaryText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.PrimaryText.NotSet');
        }
      case EntityTypes.AITaskSession:
        switch (this.typeOfActivity) {
          case ActivityType.AITaskApprovalReminder:
            return i18nBase.t(
              'activity:AITaskSession.ApprovalReminder.PrimaryText',
              this.getTransParams(globalDataCache),
            );
          default:
            return i18nBase.t('activity:Activity.PrimaryText.NotSet');
        }
      case EntityTypes.InternalAudit:
        switch (this.typeOfActivity) {
          case ActivityType.InternalAuditPlanned:
            return i18nBase.t('activity:InternalAudit.Planned.PrimaryText', this.getTransParams(globalDataCache));
          case ActivityType.InternalAuditReport:
            return i18nBase.t('activity:InternalAudit.Report.PrimaryText', this.getTransParams(globalDataCache));
          case ActivityType.InternalAuditConfirmed:
            return i18nBase.t('activity:InternalAudit.Confirmed.PrimaryText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.PrimaryText.NotSet');
        }
      default:
        return i18nBase.t('activity:Activity.PrimaryText.NotSet');
    }
  }

  getActivityTabText(globalDataCache: GlobalDataCache): string {
    switch (this.entity.typeOfEntity) {
      case EntityTypes.Task:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:Task.RecentlyChanged.ActivityTabText', this.getTransParams(globalDataCache));
          case ActivityType.RecentlyClosed:
            return i18nBase.t('activity:Task.RecentlyClosed.ActivityTabText', this.getTransParams(globalDataCache));
          case ActivityType.OverDeadline:
            return i18nBase.t('activity:Task.OverDeadline.ActivityTabText', this.getTransParams(globalDataCache));
          case ActivityType.TaskReminder:
            return i18nBase.t('activity:Task.Reminder.ActivityTabText', this.getTransParams(globalDataCache));
          case ActivityType.Refused:
            return i18nBase.t('activity:Task.Refused.ActivityTabText', this.getTransParams(globalDataCache));
          case ActivityType.RefusedInstance:
            return i18nBase.t('activity:Task.RefusedInstance.ActivityTabText', this.getTransParams(globalDataCache));
          case ActivityType.FollowUpPending:
            return i18nBase.t('activity:Task.FollowUpPending.ActivityTabText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.ActivityTabText.NotSet');
        }
      case EntityTypes.Control:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:Control.RecentlyChanged.ActivityTabText', this.getTransParams(globalDataCache));
          case ActivityType.AuditCriteriaChanged:
            return i18nBase.t(
              'activity:Control.AuditCriteriaChanged.ActivityTabText',
              this.getTransParams(globalDataCache),
            );
          default:
            return i18nBase.t('activity:Activity.ActivityTabText.NotSet');
        }
      case EntityTypes.Requirement:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t(
              'activity:Requirement.RecentlyChanged.ActivityTabText',
              this.getTransParams(globalDataCache),
            );
          default:
            return i18nBase.t('activity:Activity.ActivityTabText.NotSet');
        }
      case EntityTypes.Risk:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:Risk.RecentlyChanged.ActivityTabText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.ActivityTabText.NotSet');
        }
      case EntityTypes.Process:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:Process.RecentlyChanged.ActivityTabText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.ActivityTabText.NotSet');
        }
      case EntityTypes.Objective:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t(
              'activity:Objective.RecentlyChanged.ActivityTabText',
              this.getTransParams(globalDataCache),
            );
          default:
            return i18nBase.t('activity:Activity.ActivityTabText.NotSet');
        }
      case EntityTypes.KPI:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:KPI.RecentlyChanged.ActivityTabText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.ActivityTabText.NotSet');
        }
      case EntityTypes.Asset:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:Asset.RecentlyChanged.ActivityTabText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.ActivityTabText.NotSet');
        }
      case EntityTypes.ProcessingActivity:
        switch (this.typeOfActivity) {
          case ActivityType.ProcessingActivityApproved:
            return i18nBase.t(
              'activity:ProcessingActivity.Approved.ActivityTabText',
              this.getTransParams(globalDataCache),
            );
          case ActivityType.ProcessingActivityRejected:
            return i18nBase.t(
              'activity:ProcessingActivity.Rejected.ActivityTabText',
              this.getTransParams(globalDataCache),
            );
          default:
            return i18nBase.t('activity:Activity.ActivityTabText.NotSet');
        }
      case EntityTypes.AITaskSession:
        switch (this.typeOfActivity) {
          case ActivityType.AITaskApprovalReminder:
            return i18nBase.t(
              'activity:AITaskSession.ApprovalReminder.ActivityTabText',
              this.getTransParams(globalDataCache),
            );
          default:
            return i18nBase.t('activity:Activity.ActivityTabText.NotSet');
        }
      default:
        return i18nBase.t('activity:Activity.ActivityTabText.NotSet');
    }
  }

  getSecondaryText(globalDataCache: GlobalDataCache): string {
    switch (this.entity.typeOfEntity) {
      case EntityTypes.Task:
        switch (this.typeOfActivity) {
          case ActivityType.Assigned:
            return i18nBase.t('activity:Task.Assigned.SecondaryText', this.getTransParams(globalDataCache));
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:Task.RecentlyChanged.SecondaryText', this.getTransParams(globalDataCache));
          case ActivityType.RecentlyClosed:
            return i18nBase.t('activity:Task.RecentlyClosed.SecondaryText', this.getTransParams(globalDataCache));
          case ActivityType.OverDeadline:
            return i18nBase.t('activity:Task.OverDeadline.SecondaryText', this.getTransParams(globalDataCache));
          case ActivityType.TaskReminder:
            return i18nBase.t('activity:Task.Reminder.SecondaryText', this.getTransParams(globalDataCache));
          case ActivityType.Refused:
            return i18nBase.t('activity:Task.Refused.SecondaryText', this.getTransParams(globalDataCache));
          case ActivityType.RefusedInstance:
            return i18nBase.t('activity:Task.RefusedInstance.SecondaryText', this.getTransParams(globalDataCache));
          case ActivityType.Upcoming:
            return i18nBase.t('activity:Task.Upcoming.SecondaryText', this.getTransParams(globalDataCache));
          case ActivityType.FollowUpPending:
            return i18nBase.t('activity:Task.FollowUpPending.SecondaryText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.SecondaryText.NotSet');
        }
      case EntityTypes.Risk:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:Risk.RecentlyChanged.SecondaryText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.SecondaryText.NotSet');
        }
      case EntityTypes.Objective:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:Objective.RecentlyChanged.SecondaryText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.SecondaryText.NotSet');
        }
      case EntityTypes.Asset:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:Asset.RecentlyChanged.SecondaryText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.SecondaryText.NotSet');
        }
      case EntityTypes.Requirement:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t(
              'activity:Requirement.RecentlyChanged.SecondaryText',
              this.getTransParams(globalDataCache),
            );
          default:
            return i18nBase.t('activity:Activity.SecondaryText.NotSet');
        }
      case EntityTypes.Link:
        switch (this.typeOfActivity) {
          case ActivityType.LibraryNewVersionOwner:
            return i18nBase.t(
              'activity:Link.LibraryNewVersionOwner.SecondaryText',
              this.getTransParams(globalDataCache),
            );
          case ActivityType.LibraryReadRequest:
            return i18nBase.t('activity:Link.LibraryReadRequest.SecondaryText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.SecondaryText.NotSet');
        }
      case EntityTypes.Control:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:Control.RecentlyChanged.SecondaryText', this.getTransParams(globalDataCache));
          case ActivityType.LinkedControlUpdated:
            return i18nBase.t(
              'activity:Control.LinkedControlUpdated.SecondaryText',
              this.getTransParams(globalDataCache),
            );
          case ActivityType.AuditCriteriaChanged:
            return i18nBase.t(
              'activity:Control.AuditCriteriaChanged.SecondaryText',
              this.getTransParams(globalDataCache),
            );
          default:
            return i18nBase.t('activity:Activity.SecondaryText.NotSet');
        }
      case EntityTypes.Process:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:Process.RecentlyChanged.SecondaryText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.SecondaryText.NotSet');
        }
      case EntityTypes.KPI:
        switch (this.typeOfActivity) {
          case ActivityType.RecentlyChanged:
            return i18nBase.t('activity:KPI.RecentlyChanged.SecondaryText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.SecondaryText.NotSet');
        }
      case EntityTypes.ProcessingActivity:
        switch (this.typeOfActivity) {
          case ActivityType.ProcessingActivityApproved:
            return i18nBase.t(
              'activity:ProcessingActivity.Approved.SecondaryText',
              this.getTransParams(globalDataCache),
            );
          case ActivityType.ProcessingActivityRejected:
            return i18nBase.t(
              'activity:ProcessingActivity.Rejected.SecondaryText',
              this.getTransParams(globalDataCache),
            );
          default:
            return i18nBase.t('activity:Activity.SecondaryText.NotSet');
        }
      case EntityTypes.AITaskSession:
        switch (this.typeOfActivity) {
          case ActivityType.AITaskApprovalReminder:
            return i18nBase.t(
              'activity:AITaskSession.ApprovalReminder.SecondaryText',
              this.getTransParams(globalDataCache),
            );
          default:
            return i18nBase.t('activity:Activity.SecondaryText.NotSet');
        }
      case EntityTypes.InternalAudit:
        switch (this.typeOfActivity) {
          case ActivityType.InternalAuditPlanned:
            return i18nBase.t('activity:InternalAudit.Planned.SecondaryText', this.getTransParams(globalDataCache));
          case ActivityType.InternalAuditReport:
            return i18nBase.t('activity:InternalAudit.Report.SecondaryText', this.getTransParams(globalDataCache));
          case ActivityType.InternalAuditConfirmed:
            return i18nBase.t('activity:InternalAudit.Confirmed.SecondaryText', this.getTransParams(globalDataCache));
          default:
            return i18nBase.t('activity:Activity.SecondaryText.NotSet');
        }
      default:
        return i18nBase.t('activity:Activity.SecondaryText.NotSet');
    }
  }
}
